import { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface PollPanelProps {
  sessionId: string;
  socket: Socket | null;
  isConnected: boolean;
  numPlayers: number;
}

type PollState = 'idle' | 'preparing' | 'voting' | 'results';

const TEAM_NAMES: Record<TeamColor, string> = {
  BLUE: 'Blu',
  RED: 'Rossa',
  GREEN: 'Verde',
  YELLOW: 'Gialla',
};

const TEAM_COLORS: Record<number, TeamColor[]> = {
  2: ['RED', 'BLUE'],
  3: ['RED', 'GREEN', 'BLUE'],
  4: ['RED', 'GREEN', 'BLUE', 'YELLOW'],
};

export function PollPanel({ sessionId, socket, isConnected, numPlayers }: PollPanelProps) {
  const [options, setOptions] = useState<string[]>(['', '', '', '', '', '']);
  const [targetTeam, setTargetTeam] = useState<TeamColor | 'ALL'>('ALL');
  const [duration, setDuration] = useState(15);
  const [pollState, setPollState] = useState<PollState>('idle');
  const [activePollId, setActivePollId] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [pollResults, setPollResults] = useState<{
    options: Array<{ id: string; text: string; votes: number; percentage: number }>;
    totalVotes: number;
    winningOptionId: string | null;
  } | null>(null);

  // Conta opzioni valide (non vuote)
  const validOptionsCount = options.filter(opt => opt.trim().length > 0).length;

  // Ascolta eventi poll
  useEffect(() => {
    if (!socket) return;

    const handleTick = (data: { pollId: string; remainingSeconds: number }) => {
      if (data.pollId === activePollId) {
        setRemainingSeconds(data.remainingSeconds);
      }
    };

    const handleClosed = (data: { pollId: string }) => {
      if (data.pollId === activePollId) {
        setPollState('results');
        setRemainingSeconds(null);
      }
    };

    socket.on('poll:tick', handleTick);
    socket.on('poll:closed', handleClosed);

    return () => {
      socket.off('poll:tick', handleTick);
      socket.off('poll:closed', handleClosed);
    };
  }, [socket, activePollId]);

  // Fetch risultati durante voting (polling) e results (una volta)
  useEffect(() => {
    if (!activePollId || (pollState !== 'voting' && pollState !== 'results')) return;

    const fetchResults = async () => {
      try {
        const response = await fetch(`/api/polls/${activePollId}/results`);
        if (response.ok) {
          const data = await response.json();
          setPollResults(data);
        }
      } catch (error) {
        console.error('[PollPanel] Error fetching results:', error);
      }
    };

    fetchResults();

    if (pollState === 'voting') {
      const interval = setInterval(fetchResults, 1000);
      return () => clearInterval(interval);
    }
  }, [pollState, activePollId]);

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleStartPoll = async () => {
    if (validOptionsCount < 2) {
      toast.error('Inserisci almeno 2 opzioni');
      return;
    }

    if (!isConnected) {
      toast.error('Socket non connesso');
      return;
    }

    try {
      // Filtra solo opzioni non vuote
      const validOptions = options.filter(opt => opt.trim().length > 0);

      // Crea il poll
      const createResponse = await fetch('/api/polls/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          targetTeam: targetTeam === 'ALL' ? null : targetTeam,
          options: validOptions,
          durationSeconds: duration,
        }),
      });

      if (!createResponse.ok) {
        const error = await createResponse.json();
        throw new Error(error.error || 'Errore creazione sondaggio');
      }

      const { poll } = await createResponse.json();
      setActivePollId(poll.id);
      setPollState('preparing');

      // Avvia il poll (3s preparazione + countdown)
      const startResponse = await fetch(`/api/polls/${poll.id}/start`, {
        method: 'POST',
      });

      if (!startResponse.ok) {
        const error = await startResponse.json();
        throw new Error(error.error || 'Errore avvio sondaggio');
      }

      toast.success('Sondaggio avviato!');

      // Dopo 3 secondi passa a voting
      setTimeout(() => {
        setPollState('voting');
        setRemainingSeconds(duration);
      }, 3000);

    } catch (error: any) {
      console.error('[PollPanel] Error starting poll:', error);
      toast.error(error.message || 'Errore avvio sondaggio');
      setPollState('idle');
    }
  };

  const handleReset = () => {
    setOptions(['', '', '', '', '', '']);
    setTargetTeam('ALL');
    setDuration(15);
    setPollState('idle');
    setActivePollId(null);
    setRemainingSeconds(null);
    setPollResults(null);

    // Torna a standby sul display
    if (socket && isConnected) {
      socket.emit('admin:set-projection', {
        sessionId,
        mode: { type: 'standby' },
      });
    }

    toast.success('Sondaggio resettato');
  };

  const handleForceClose = async () => {
    if (!activePollId) return;

    try {
      await fetch(`/api/polls/${activePollId}/close`, {
        method: 'POST',
      });
      setPollState('results');
      setRemainingSeconds(null);
      toast.success('Sondaggio chiuso');
    } catch (error) {
      console.error('[PollPanel] Error closing poll:', error);
      toast.error('Errore chiusura sondaggio');
    }
  };

  const availableTeams = TEAM_COLORS[numPlayers] || TEAM_COLORS[2];

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">Sondaggio</h3>
        {pollState === 'idle' && (
          <button
            onClick={() => setOptions([
              'La grande Forgia',
              'Il Tempio maggiore',
              'La Gilda degli Scavatori',
              'I Bagni di Vapore',
              'Il grande mercato',
              'La fossa',
            ])}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1.5 rounded-lg transition-all"
          >
            Preset zone
          </button>
        )}
      </div>

      {pollState === 'idle' && (
        <>
          {/* Opzioni */}
          <div className="space-y-3 mb-6">
            <label className="text-gray-400 text-sm font-semibold">
              Opzioni (min 2, max 6)
            </label>
            {options.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-gray-500 text-sm w-6">{index + 1}.</span>
                <input
                  type="text"
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Opzione ${index + 1}`}
                  className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            ))}
            <p className="text-gray-500 text-xs">
              Opzioni compilate: {validOptionsCount}/6
            </p>
          </div>

          {/* Target squadra */}
          <div className="mb-6">
            <label className="text-gray-400 text-sm font-semibold block mb-2">
              Chi vota?
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTargetTeam('ALL')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  targetTeam === 'ALL'
                    ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Tutti
              </button>
              {availableTeams.map((team) => (
                <button
                  key={team}
                  onClick={() => setTargetTeam(team)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    targetTeam === team
                      ? `bg-${team.toLowerCase()}-600 text-white ring-2 ring-${team.toLowerCase()}-400`
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  style={{
                    backgroundColor: targetTeam === team
                      ? team === 'BLUE' ? '#2563eb'
                        : team === 'RED' ? '#dc2626'
                        : team === 'GREEN' ? '#16a34a'
                        : '#ca8a04'
                      : undefined,
                  }}
                >
                  {TEAM_NAMES[team]}
                </button>
              ))}
            </div>
          </div>

          {/* Durata */}
          <div className="mb-6">
            <label className="text-gray-400 text-sm font-semibold block mb-2">
              Durata voto (secondi)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={duration}
              onChange={(e) => setDuration(e.target.value as any)}
              onBlur={(e) => { const n = parseInt(e.target.value); setDuration(isNaN(n) ? 15 : Math.max(5, Math.min(60, n))); }}
              className="w-24 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <span className="text-gray-500 text-sm ml-2">secondi</span>
          </div>

          {/* Pulsanti */}
          <div className="flex gap-3">
            <button
              onClick={handleStartPoll}
              disabled={validOptionsCount < 2 || !isConnected}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-bold transition-all"
            >
              Avvia Sondaggio
            </button>
            <button
              onClick={handleReset}
              className="bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-semibold transition-all"
            >
              Reset
            </button>
          </div>
        </>
      )}

      {pollState === 'preparing' && (
        <div className="text-center py-8">
          <div className="text-6xl mb-4 animate-pulse">3</div>
          <p className="text-2xl text-white font-bold mb-2">Prepararsi al voto!</p>
          <p className="text-gray-400">
            {targetTeam === 'ALL' ? 'Tutti i giocatori' : `Squadra ${TEAM_NAMES[targetTeam]}`}
          </p>
        </div>
      )}

      {pollState === 'voting' && (
        <div className="py-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-lg text-white font-bold">Votazione in corso</p>
            <span className="text-3xl font-bold text-yellow-400">
              {remainingSeconds !== null ? remainingSeconds : '...'}s
            </span>
          </div>
          <div className="space-y-3 mb-4">
            {options.filter(opt => opt.trim()).map((opt, i) => {
              const liveOpt = pollResults?.options.find(o => o.text === opt);
              const pct = liveOpt?.percentage ?? 0;
              const votes = liveOpt?.votes ?? 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-200">{opt}</span>
                    <span className="text-sm font-bold text-gray-400">
                      {votes} ({pct}%)
                    </span>
                  </div>
                  <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 bg-purple-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {pollResults && (
            <p className="text-gray-500 text-xs mb-4">
              Voti ricevuti: {pollResults.totalVotes}
            </p>
          )}
          <button
            onClick={handleForceClose}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-6 rounded-lg font-semibold transition-all"
          >
            Chiudi ora
          </button>
        </div>
      )}

      {pollState === 'results' && (
        <div className="py-4">
          <p className="text-lg text-white font-bold mb-4">Risultati</p>
          {pollResults ? (
            <>
              <div className="space-y-3 mb-4">
                {[...pollResults.options]
                  .sort((a, b) => b.percentage - a.percentage)
                  .map((opt) => {
                    const isWinner = opt.id === pollResults.winningOptionId;
                    return (
                      <div key={opt.id}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-semibold ${isWinner ? 'text-yellow-300' : 'text-gray-200'}`}>
                            {opt.text}
                          </span>
                          <span className={`text-sm font-bold ${isWinner ? 'text-yellow-300' : 'text-gray-400'}`}>
                            {opt.votes} ({opt.percentage}%)
                          </span>
                        </div>
                        <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${isWinner ? 'bg-yellow-400' : 'bg-purple-500'}`}
                            style={{ width: `${opt.percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
              <p className="text-gray-500 text-xs mb-4">
                Totale voti: {pollResults.totalVotes}
              </p>
            </>
          ) : (
            <p className="text-gray-400 text-sm mb-4">Caricamento risultati...</p>
          )}
          <button
            onClick={handleReset}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-8 rounded-lg font-bold transition-all"
          >
            Nuovo Sondaggio
          </button>
        </div>
      )}

      {!isConnected && pollState === 'idle' && (
        <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
          <p className="text-yellow-300 text-sm">
            Socket non connesso. Il sondaggio non può essere avviato.
          </p>
        </div>
      )}
    </div>
  );
}
